// MatchPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/customButtons/Button';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_API_URL;

// Structured data generator
const generateStructuredData = (products, user) => {
  const orderItems = products.flatMap(product => 
    (product.matches || []).map(match => ({
      "@type": "Order",
      "orderNumber": match._id,
      "orderStatus": `https://schema.org/OrderStatus/${match.status.charAt(0).toUpperCase() + match.status.slice(1)}`,
      "orderedItem": {
        "@type": "Product",
        "name": product.name,
        "image": product.image || 'https://placehold.co/200x200/f3f4f6/555555?text=Product'
      },
      "seller": {
        "@type": "Organization",
        "name": user?.name || "CropSer Seller"
      }
    }))
  );

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Order Management Dashboard",
    "description": "Manage your agricultural product orders and track order status",
    "numberOfItems": orderItems.length,
    "itemListElement": orderItems.slice(0, 10)
  };
};

// Simple Modal Component
const Modal = ({ isOpen, onClose, title, children, type = 'info' }) => {
    if (!isOpen) return null;

    const getIcon = () => {
        switch (type) {
            case 'success':
                return (
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                );
            case 'warning':
                return (
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                        <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                );
            case 'error':
                return (
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                );
            default:
                return (
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
        }
    };

    return (
        <div 
            className="fixed z-50 inset-0 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    onClick={onClose}
                    aria-hidden="true"
                ></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div 
                    className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                    role="document"
                >
                    <div className="sm:flex sm:items-start">
                        {getIcon()}
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h3 
                                className="text-lg leading-6 font-medium text-gray-900 font-heading"
                                id="modal-title"
                            >
                                {title}
                            </h3>
                            <div className="mt-2">
                                <div className="text-sm text-gray-500 font-sans">
                                    {children}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-accent sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={onClose}
                            aria-label="Close modal and confirm"
                        >
                            OK
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel" }) => {
    if (!isOpen) return null;

    return (
        <div 
            className="fixed z-50 inset-0 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirmation-modal-title"
        >
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 
                                className="text-lg leading-6 font-medium text-gray-900 font-heading"
                                id="confirmation-modal-title"
                            >
                                {title}
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 font-sans">
                                    {message}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 font-accent sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={() => {
                                onConfirm();
                                onClose();
                            }}
                            aria-label={`Confirm ${confirmText}`}
                        >
                            {confirmText}
                        </button>
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-accent sm:mt-0 sm:w-auto sm:text-sm"
                            onClick={onClose}
                            aria-label={`Cancel and close modal`}
                        >
                            {cancelText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const MatchPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { token, user } = useAuth();

    const [loading, setLoading] = useState(false);
    const [matchedProducts, setMatchedProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [statusUpdateLoading, setStatusUpdateLoading] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);

    // Modal states
    const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        confirmText: 'Confirm',
        cancelText: 'Cancel'
    });
    const [whatsappModal, setWhatsappModal] = useState({ isOpen: false, link: '' });

    // Generate structured data
    const structuredData = generateStructuredData(matchedProducts, user);

    // SEO metadata
    const pageTitle = `Order Management - ${matchedProducts.length} Products with Orders | CropSer`;
    const pageDescription = `Manage ${matchedProducts.length} agricultural product orders. Track order status, communicate with buyers, and update order progress on CropSer.`;
    const canonicalUrl = `https://cropser.com${location.pathname}`;

    useEffect(() => {
        fetchMatchedProducts();

        // Set up polling for real-time updates
        const interval = setInterval(() => {
            fetchMatchedProducts();
        }, 15000); // Update every 15 seconds

        return () => clearInterval(interval);
    }, [token]);

    useEffect(() => {
        if (matchedProducts.length > 0 && !selectedProduct) {
            setSelectedProduct(matchedProducts[0]);
        }
    }, [matchedProducts, selectedProduct]);

    const showModal = (title, message, type = 'info') => {
        setModal({ isOpen: true, title, message, type });
    };

    const showConfirmationModal = (title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel') => {
        setConfirmationModal({ isOpen: true, title, message, onConfirm, confirmText, cancelText });
    };

    const closeModal = () => {
        setModal({ ...modal, isOpen: false });
    };

    const closeConfirmationModal = () => {
        setConfirmationModal({ ...confirmationModal, isOpen: false });
    };

    const fetchMatchedProducts = async () => {
        if (!token) {
            console.log('❌ No authentication token available');
            return;
        }

        setLoading(true);
        try {
            console.log('🔍 Fetching all matched products...');
            const response = await fetch(`${API_URL}/api/products/my-products/matched`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('📡 Response Status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const data = await response.json();
            console.log('✅ All matched products found:', data);

            let productsArray = [];

            if (Array.isArray(data)) {
                productsArray = data;
            } else if (data.products && Array.isArray(data.products)) {
                productsArray = data.products;
            }

            console.log(`📦 Total products with matches: ${productsArray.length}`);

            // Debug each product
            productsArray.forEach((product, index) => {
                console.log(`\n📦 Product ${index + 1}: ${product.name}`);
                const allMatches = product.matches || [];
                console.log(`   Total matches: ${allMatches.length}`);

                const statusCount = {};
                allMatches.forEach(match => {
                    statusCount[match.status] = (statusCount[match.status] || 0) + 1;
                });
                console.log(`   Status counts:`, statusCount);
            });

            setMatchedProducts(productsArray);
            setLastUpdated(new Date().toLocaleTimeString());

        } catch (error) {
            console.error('❌ Error fetching matched products:', error);
            showModal('Error', `Failed to load orders: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Enhanced status update function
    const updateOrderStatus = async (productId, matchId, newStatus) => {
        setStatusUpdateLoading(prev => ({ ...prev, [matchId]: true }));

        try {
            const response = await fetch(
                `${API_URL}/api/products/${productId}/matches/${matchId}/status`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ status: newStatus })
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to update status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                // Update local state immediately
                setMatchedProducts(prevProducts =>
                    prevProducts.map(product => {
                        if (product._id === productId) {
                            return {
                                ...product,
                                matches: product.matches.map(match =>
                                    match._id === matchId
                                        ? { ...match, status: newStatus, updatedAt: new Date().toISOString() }
                                        : match
                                )
                            };
                        }
                        return product;
                    })
                );

                console.log(`✅ Order ${matchId} updated to ${newStatus}`);

                // Handle WhatsApp notification result with modals
                if (result.alert?.sent) {
                    showModal(
                        'Success',
                        `Order status updated to ${newStatus} and buyer notified via WhatsApp!`,
                        'success'
                    );
                } else if (result.alert?.whatsappLink) {
                    setWhatsappModal({
                        isOpen: true,
                        link: result.alert.whatsappLink,
                        status: newStatus
                    });
                } else {
                    showModal(
                        'Success',
                        `Order status updated to ${newStatus}!`,
                        'success'
                    );
                }

                // Refresh data to ensure consistency
                setTimeout(() => fetchMatchedProducts(), 1000);
            }
        } catch (error) {
            console.error('Error updating order status:', error);
            showModal('Error', `Failed to update order status: ${error.message}`, 'error');
        } finally {
            setStatusUpdateLoading(prev => ({ ...prev, [matchId]: false }));
        }
    };

    // Enhanced Status Manager Component
    const StatusManager = ({ match, product }) => {
        const [showStatusMenu, setShowStatusMenu] = useState(false);

        const getAvailableActions = (currentStatus) => {
            const actions = {
                matched: [
                    { status: 'accepted', label: 'Accept Order', color: 'bg-blue-500 hover:bg-blue-600' },
                    { status: 'rejected', label: 'Reject Order', color: 'bg-red-500 hover:bg-red-600' }
                ],
                accepted: [
                    { status: 'processing', label: 'Start Processing', color: 'bg-white hover:bg-purple-600' },
                    { status: 'cancelled', label: 'Cancel Order', color: 'bg-red-500 hover:bg-red-600' }
                ],
                processing: [
                    { status: 'shipped', label: 'Mark as Shipped', color: 'bg-white-500 hover:bg-gray-100' },
                    { status: 'cancelled', label: 'Cancel Order', color: 'bg-red-500 hover:bg-red-600' }
                ],
                shipped: [
                    { status: 'delivered', label: 'Mark as Delivered', color: 'bg-green-500 hover:bg-green-600' }
                ],
                delivered: [],
                rejected: [],
                cancelled: []
            };

            return actions[currentStatus] || [];
        };

        const availableActions = getAvailableActions(match.status);
        const isLoading = statusUpdateLoading[match._id];

        const getStatusColor = (status) => {
            const colors = {
                matched: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                accepted: 'bg-blue-100 text-blue-800 border-blue-200',
                processing: 'bg-purple-100 text-purple-800 border-purple-200',
                shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
                delivered: 'bg-green-100 text-green-800 border-green-200',
                rejected: 'bg-red-100 text-red-800 border-red-200',
                cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
            };
            return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
        };

        const handleStatusChange = (action) => {
            showConfirmationModal(
                'Confirm Action',
                `Are you sure you want to ${action.label.toLowerCase()}?`,
                () => {
                    updateOrderStatus(product._id, match._id, action.status);
                    setShowStatusMenu(false);
                },
                action.label,
                'Cancel'
            );
        };

        return (
            <div className="relative">
                <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(match.status)} font-sans`}>
                        {match.status.charAt(0).toUpperCase() + match.status.slice(1)}
                        {isLoading && ' 🔄'}
                    </span>

                    {availableActions.length > 0 && (
                        <button
                            onClick={() => setShowStatusMenu(!showStatusMenu)}
                            disabled={isLoading}
                            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                            aria-label="Change order status"
                            aria-expanded={showStatusMenu}
                            aria-haspopup="true"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    )}
                </div>

                {showStatusMenu && availableActions.length > 0 && (
                    <div 
                        className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[160px]"
                        role="menu"
                        aria-label="Order status options"
                    >
                        {availableActions.map(action => (
                            <button
                                key={action.status}
                                onClick={() => handleStatusChange(action)}
                                className={`w-full text-left px-4 py-2 text-sm text-gray-600 ${action.color} first:rounded-t-lg last:rounded-b-lg transition-colors font-sans`}
                                role="menuitem"
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // WhatsApp Modal Component
    const WhatsAppModal = ({ isOpen, onClose, link, status }) => {
        if (!isOpen) return null;

        return (
            <div 
                className="fixed z-50 inset-0 overflow-y-auto"
                role="dialog"
                aria-modal="true"
                aria-labelledby="whatsapp-modal-title"
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                    <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="h-6 w-6 text-green-600">
                                    <path fill="currentColor" d="M19.05 4.91A9.816 9.816 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21c5.46 0 9.91-4.45 9.91-9.91c0-2.65-1.03-5.14-2.9-7.01m-7.01 15.24c-1.48 0-2.93-.4-4.2-1.15l-.3-.18l-3.12.82l.83-3.04l-.2-.31a8.264 8.264 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24c2.2 0 4.27.86 5.82 2.42a8.183 8.183 0 0 1 2.41 5.83c.02 4.54-3.68 8.23-8.22 8.23m4.52-6.16c-.25-.12-1.47-.72-1.69-.81c-.23-.08-.39-.12-.56.12c-.17.25-.64.81-.78.97c-.14.17-.29.19-.54.06c-.25-.12-1.05-.39-1.99-1.23c-.74-.66-1.23-1.47-1.38-1.72c-.14-.25-.02-.38.11-.51c.11-.11.25-.29.37-.43s.17-.25.25-.41c.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31c-.22.25-.86.85-.86 2.07s.89 2.4 1.01 2.56c.12.17 1.75 2.67 4.23 3.74c.59.26 1.05.41 1.41.52c.59.19 1.13.16 1.56.1c.48-.07 1.47-.6 1.67-1.18c.21-.58.21-1.07.14-1.18s-.22-.16-.47-.28" />
                                </svg>
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 
                                    className="text-lg leading-6 font-medium text-gray-900 font-heading"
                                    id="whatsapp-modal-title"
                                >
                                    Order Updated to {status.charAt(0).toUpperCase() + status.slice(1)}
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500 font-sans">
                                        A WhatsApp message has been prepared for the buyer. Would you like to open WhatsApp to send it now?
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 font-accent sm:ml-3 sm:w-auto sm:text-sm"
                                onClick={() => {
                                    window.open(link, '_blank');
                                    onClose();
                                }}
                                aria-label="Open WhatsApp to send message"
                            >
                                Open WhatsApp
                            </button>
                            <button
                                type="button"
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-accent sm:mt-0 sm:w-auto sm:text-sm"
                                onClick={onClose}
                                aria-label="Cancel and close modal"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Enhanced helper function to get buyer info
    const getBuyerInfo = (match, product) => {
        let buyerInfo = null;
        let deliveryInfo = null;

        console.log('🔍 Debugging getBuyerInfo:', {
            matchId: match._id,
            matchBuyer: match.buyer,
            matchBuyerType: typeof match.buyer,
            productDeliveryRequests: product.deliveryRequests?.length
        });

        // First, try to get buyer info from match
        if (match.buyer) {
            if (typeof match.buyer === 'object' && match.buyer.name) {
                buyerInfo = match.buyer;
            } else if (typeof match.buyer === 'string') {
                // Store the buyer ID for matching with delivery requests
                buyerInfo = { _id: match.buyer };
            }
        }

        // Look for delivery request information - FIXED MATCHING LOGIC
        if (product.deliveryRequests && product.deliveryRequests.length > 0) {
            console.log('📦 Available delivery requests:', product.deliveryRequests);

            // Find delivery request by buyer ID
            const deliveryRequest = product.deliveryRequests.find(dr => {
                if (!dr.buyer) return false;

                const drBuyerId = typeof dr.buyer === 'object' ? dr.buyer._id?.toString() : dr.buyer.toString();
                const matchBuyerId = typeof match.buyer === 'object' ? match.buyer._id?.toString() : match.buyer.toString();

                console.log('🔄 Comparing:', {
                    drBuyerId,
                    matchBuyerId,
                    match: drBuyerId === matchBuyerId
                });

                return drBuyerId === matchBuyerId;
            });

            console.log('✅ Found delivery request:', deliveryRequest);

            if (deliveryRequest) {
                // Get buyer info from delivery request if not already found
                if (!buyerInfo && deliveryRequest.buyer && typeof deliveryRequest.buyer === 'object') {
                    buyerInfo = deliveryRequest.buyer;
                }

                // Extract delivery information
                deliveryInfo = {
                    deliveryAddress: deliveryRequest.deliveryAddress,
                    specialInstructions: deliveryRequest.specialInstructions,
                    status: deliveryRequest.status,
                    requestedAt: deliveryRequest.requestedAt
                };

                console.log('📬 Delivery info found:', deliveryInfo);
            } else {
                console.log('❌ No matching delivery request found for this match');
            }
        } else {
            console.log('❌ No delivery requests available in product');
        }

        return {
            ...buyerInfo,
            deliveryInfo
        };
    };

    // Enhanced WhatsApp chat function
    const startWhatsAppChat = (buyerInfo, product, match) => {
        const buyerName = buyerInfo?.name || 'Buyer';
        const productName = product.name;
        const quantity = match.quantity;
        const unit = match.unit;
        const price = formatCurrency(match.offeredPrice || match.price);
        const status = match.status;

        // Dynamic message based on order status
        const statusMessages = {
            matched: `Hello ${buyerName}! This is ${user?.name || 'the seller'} from CropSer. I've seen your order for ${quantity} ${unit} of ${productName} (${price}). I'm reviewing it now.`,
            accepted: `Hello ${buyerName}! This is ${user?.name || 'the seller'} from CropSer. I've accepted your order for ${quantity} ${unit} of ${productName} (${price}). We'll start processing it soon.`,
            processing: `Hello ${buyerName}! Your order for ${quantity} ${unit} of ${productName} is now being processed. Expected dispatch soon.`,
            shipped: `Hello ${buyerName}! Your order for ${productName} has been shipped. It should arrive soon.`,
            delivered: `Hello ${buyerName}! Your order for ${productName} has been delivered. Thank you for your business!`
        };

        const message = statusMessages[status] ||
            `Hello ${buyerName}! This is ${user?.name || 'the seller'} from CropSer. I'm contacting you about your order for ${quantity} ${unit} of ${productName} (${price}).`;

        const encodedMessage = encodeURIComponent(message);
        const defaultPhone = '254700000000';
        const buyerPhone = buyerInfo?.phone || buyerInfo?.mobile || defaultPhone;
        const cleanPhone = buyerPhone.replace(/\D/g, '');
        const phoneWithCountryCode = cleanPhone.startsWith('254') ? cleanPhone : `254${cleanPhone.substring(cleanPhone.length - 9)}`;
        const whatsappUrl = `https://wa.me/${phoneWithCountryCode}?text=${encodedMessage}`;

        window.open(whatsappUrl, '_blank');
    };

    const formatCurrency = (amount) => {
        if (typeof amount !== 'number') return 'Kshs. Negotiable';
        return `Kshs. ${amount.toLocaleString('en-KE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        })}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Product Selection Component
    const ProductSelection = () => {
        if (matchedProducts.length === 0) return null;

        return (
            <section aria-labelledby="product-selection-heading">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 
                            id="product-selection-heading"
                            className="text-[14px] font-semibold text-gray-900 font-heading"
                        >
                            Select Product
                        </h2>
                        <div className="flex items-center gap-4 text-xs text-gray-500 font-sans">
                            <span>Last updated: {lastUpdated || 'Never'}</span>
                            <button
                                onClick={fetchMatchedProducts}
                                className="flex items-center gap-1 text-primary hover:text-primary-light font-accent"
                                aria-label="Refresh product list"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Refresh
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" role="list" aria-label="Products with orders">
                        {matchedProducts.map((productItem) => {
                            const totalMatches = productItem.matches?.length || 0;

                            // Count matches by status
                            const statusCount = {};
                            productItem.matches?.forEach(match => {
                                statusCount[match.status] = (statusCount[match.status] || 0) + 1;
                            });

                            return (
                                <article 
                                    key={productItem._id}
                                    className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedProduct?._id === productItem._id
                                        ? 'border-primary bg-primary-light bg-opacity-10'
                                        : 'border-gray-200 hover:border-primary'
                                        }`}
                                    onClick={() => setSelectedProduct(productItem)}
                                    role="listitem"
                                    aria-label={`Product: ${productItem.name} with ${totalMatches} orders`}
                                    tabIndex={0}
                                    onKeyPress={(e) => e.key === 'Enter' && setSelectedProduct(productItem)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <img
                                            src={productItem.image || 'https://placehold.co/200x200/f3f4f6/555555?text=Product'}
                                            alt={`Image of ${productItem.name}`}
                                            className="w-12 h-12 rounded-lg object-cover"
                                            loading="lazy"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-gray-900 text-sm font-heading">
                                                {productItem.name}
                                            </h3>
                                            <p className="text-xs text-gray-600 mt-1 font-sans">
                                                {totalMatches} order{totalMatches !== 1 ? 's' : ''}
                                            </p>
                                            <div className="flex flex-wrap gap-1 mt-2" aria-label="Order status counts">
                                                {Object.entries(statusCount).map(([status, count]) => (
                                                    <span 
                                                        key={status} 
                                                        className={`px-2 py-1 rounded text-xs font-sans ${status === 'matched' ? 'bg-yellow-100 text-yellow-800' :
                                                            status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                                                                status === 'processing' ? 'bg-purple-100 text-purple-800' :
                                                                    status === 'shipped' ? 'bg-indigo-100 text-indigo-800' :
                                                                        status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                                            'bg-gray-100 text-gray-800'
                                                            }`}
                                                        aria-label={`${count} orders with status ${status}`}
                                                    >
                                                        {count} {status}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>
        );
    };

    // Enhanced Buyers List Component
    const BuyersList = () => {
        if (!selectedProduct) return null;

        const allMatches = selectedProduct.matches || [];

        // Sort matches: active first, then by date
        const sortedMatches = [...allMatches].sort((a, b) => {
            const activeStatuses = ['matched', 'accepted', 'processing', 'shipped'];
            const aIsActive = activeStatuses.includes(a.status);
            const bIsActive = activeStatuses.includes(b.status);

            if (aIsActive && !bIsActive) return -1;
            if (!aIsActive && bIsActive) return 1;

            return new Date(b.createdAt) - new Date(a.createdAt);
        });

        return (
            <section aria-labelledby="buyers-list-heading">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 
                            id="buyers-list-heading"
                            className="text-[14px] font-semibold text-gray-900 font-heading"
                        >
                            Orders for <span className='text-primary'>{selectedProduct.name}</span>
                        </h2>
                        <span className="text-xs text-gray-500 font-sans">
                            {sortedMatches.length} total order{sortedMatches.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {sortedMatches.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500 font-sans">No orders found for this product.</p>
                        </div>
                    ) : (
                        <div className="space-y-4" role="list" aria-label="Order list">
                            {sortedMatches.map((match, index) => {
                                const buyerInfo = getBuyerInfo(match, selectedProduct);
                                const buyerName = buyerInfo?.name || 'Buyer';
                                const buyerInitial = buyerName.charAt(0).toUpperCase();
                                const hasDeliveryInfo = buyerInfo.deliveryInfo && buyerInfo.deliveryInfo.deliveryAddress;

                                return (
                                    <article 
                                        key={match._id} 
                                        className={`border rounded-lg p-4 transition-all ${['matched', 'accepted', 'processing', 'shipped'].includes(match.status)
                                            ? 'border-gray-200 hover:shadow-md bg-white'
                                            : 'border-gray-100 bg-gray-50'
                                            }`}
                                        role="listitem"
                                        itemScope
                                        itemType="https://schema.org/Order"
                                    >
                                        <meta itemProp="orderNumber" content={match._id} />
                                        <meta itemProp="orderStatus" content={match.status} />
                                        
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                <div 
                                                    className="w-10 h-10 bg-primary bg-opacity-10 rounded-full flex items-center justify-center"
                                                    aria-hidden="true"
                                                >
                                                    <span className="text-primary font-semibold text-sm font-heading">
                                                        {buyerInitial}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h3 
                                                        className="font-semibold text-gray-900 font-heading"
                                                        itemProp="customer"
                                                    >
                                                        {buyerName}
                                                    </h3>
                                                    <p className="text-xs text-gray-600 font-sans">
                                                        Ordered <time itemProp="orderDate" dateTime={match.createdAt}>
                                                            {formatDate(match.createdAt)}
                                                        </time>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-semibold text-gray-900 font-heading" itemProp="price">
                                                    {formatCurrency(match.offeredPrice || match.price)}
                                                </p>
                                                <p className="text-xs text-gray-600 font-sans" itemProp="orderedItem">
                                                    {match.quantity} {match.unit}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Delivery Information Section - Always Visible When Available */}
                                        {hasDeliveryInfo && (
                                            <div 
                                                className="mb-3 p-3 bg-primary bg-opacity-5 rounded-lg border border-primary border-opacity-20"
                                                itemScope
                                                itemType="https://schema.org/ParcelDelivery"
                                            >
                                                <div className="space-y-2 text-sm">
                                                    <div className='flex gap-2 items-center'>
                                                        <span>
                                                            <p className="text-primary text-xs flex gap-2 font-bold font-heading">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                </svg>
                                                                Delivery Address:</p>
                                                        </span>
                                                        <p 
                                                            className="text-primary text-xs font-sans"
                                                            itemProp="deliveryAddress"
                                                        >
                                                            {buyerInfo.deliveryInfo.deliveryAddress}
                                                        </p>
                                                    </div>

                                                    {buyerInfo.deliveryInfo.specialInstructions && (
                                                        <div className='flex gap-2 items-center'>
                                                            <p className="text-primary text-xs flex items-center gap-2 font-bold font-heading">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                                Special Instructions:
                                                            </p>

                                                            <p className="text-primary text-xs font-sans">
                                                                {buyerInfo.deliveryInfo.specialInstructions}
                                                            </p>
                                                        </div>

                                                    )}
                                                </div>

                                                {buyerInfo.deliveryInfo.requestedAt && (
                                                    <p className="text-xs font-bold text-primary mt-2 flex gap-2 font-heading">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        Requested: <time dateTime={buyerInfo.deliveryInfo.requestedAt}>
                                                            {formatDate(buyerInfo.deliveryInfo.requestedAt)}
                                                        </time>
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* No Delivery Info Message - Only show if no delivery info exists */}
                                        {!hasDeliveryInfo && (
                                            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                <p className="text-sm text-gray-600 flex items-center gap-2 font-sans">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                                    </svg>
                                                    No delivery information provided
                                                </p>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 text-sm">
                                            <div>
                                                <p className="text-gray-600 font-sans">
                                                    <strong>Order ID:</strong>
                                                    <span className="font-mono ml-1 text-xs bg-gray-100 px-2 py-1 rounded">
                                                        {match._id?.slice(-8)}
                                                    </span>
                                                </p>
                                            </div>
                                            <div>
                                                <StatusManager match={match} product={selectedProduct} />
                                            </div>
                                            <div>
                                                <p className="text-gray-600 font-sans">
                                                    <strong>Updated:</strong> <time dateTime={match.updatedAt}>{formatDate(match.updatedAt)}</time>
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                                            <Button
                                                onClick={() => startWhatsAppChat(buyerInfo, selectedProduct, match)}
                                                variant="primary"
                                                className="text-tiny gap-2 text-white bg-green-600 hover:bg-green-700 border-green-600 font-accent"
                                                aria-label={`Contact ${buyerName} via WhatsApp about order`}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="size-4">
                                                    <path fill="currentColor" d="M19.05 4.91A9.816 9.816 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21c5.46 0 9.91-4.45 9.91-9.91c0-2.65-1.03-5.14-2.9-7.01m-7.01 15.24c-1.48 0-2.93-.4-4.2-1.15l-.3-.18l-3.12.82l.83-3.04l-.2-.31a8.264 8.264 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24c2.2 0 4.27.86 5.82 2.42a8.183 8.183 0 0 1 2.41 5.83c.02 4.54-3.68 8.23-8.22 8.23m4.52-6.16c-.25-.12-1.47-.72-1.69-.81c-.23-.08-.39-.12-.56.12c-.17.25-.64.81-.78.97c-.14.17-.29.19-.54.06c-.25-.12-1.05-.39-1.99-1.23c-.74-.66-1.23-1.47-1.38-1.72c-.14-.25-.02-.38.11-.51c.11-.11.25-.29.37-.43s.17-.25.25-.41c.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31c-.22.25-.86.85-.86 2.07s.89 2.4 1.01 2.56c.12.17 1.75 2.67 4.23 3.74c.59.26 1.05.41 1.41.52c.59.19 1.13.16 1.56.1c.48-.07 1.47-.6 1.67-1.18c.21-.58.21-1.07.14-1.18s-.22-.16-.47-.28" />
                                                </svg>
                                                WhatsApp Buyer
                                            </Button>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        );
    };

    // Loading state
    if (loading && matchedProducts.length === 0) {
        return (
            <>
                <Helmet>
                    <title>Loading Orders | CropSer</title>
                    <meta name="description" content="Loading your agricultural product orders..." />
                </Helmet>
                <div className="min-h-screen bg-neutral flex items-center justify-center">
                    <div className="text-center">
                        <svg className="animate-spin h-8 w-8 text-primary mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-gray-600 font-sans">Loading your orders...</p>
                    </div>
                </div>
            </>
        );
    }

    // No matches state
    if (!loading && matchedProducts.length === 0) {
        return (
            <>
                <Helmet>
                    <title>No Orders Yet | CropSer</title>
                    <meta name="description" content="You don't have any orders yet. When farmers order your products, they will appear here for management." />
                    <meta name="robots" content="noindex" />
                </Helmet>
                <div className="min-h-screen bg-neutral flex items-center justify-center px-4">
                    <div className="text-center max-w-xl">
                        <div className="mx-auto w-16 h-16 mb-4 text-gray-300">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-full h-full">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-700 mb-3 font-heading">No Orders Yet</h2>
                        <div className="text-gray-500 text-[14px] space-y-2 mb-2 max-w-xl font-sans">
                            <p>When farmers order your products, they will appear here for you to manage.</p>
                            <p>You can track order status, communicate with buyers, and update order progress.</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {/* SEO Head Section */}
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                
                {/* Open Graph */}
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="CropSer" />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                
                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
                
                {/* Additional Meta Tags */}
                <meta name="robots" content="index, follow" />
                <meta name="keywords" content="order management, agricultural products, farm orders, crop sales, order tracking" />
            </Helmet>

            <div className="min-h-screen bg-neutral py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto mt-14">
                    {/* Header with proper heading hierarchy */}
                    <header className="mb-8">
                        <h1 className="text-xl sm:text-xl font-bold text-primary font-heading">
                            Order Management
                        </h1>
                        <p className="text-gray-500 text-[14px] mt-2 font-sans">
                            Track and manage all your product orders from farmers.
                            {lastUpdated && (
                                <span className="text-xs text-gray-500 ml-2">
                                    (Updated: <time dateTime={new Date().toISOString()}>{lastUpdated}</time>)
                                </span>
                            )}
                        </p>
                    </header>

                    {/* Product Selection */}
                    <ProductSelection />

                    {/* Buyers List */}
                    <BuyersList />

                    {/* Modals */}
                    <Modal
                        isOpen={modal.isOpen}
                        onClose={closeModal}
                        title={modal.title}
                        type={modal.type}
                    >
                        {modal.message}
                    </Modal>

                    <ConfirmationModal
                        isOpen={confirmationModal.isOpen}
                        onClose={closeConfirmationModal}
                        onConfirm={confirmationModal.onConfirm}
                        title={confirmationModal.title}
                        message={confirmationModal.message}
                        confirmText={confirmationModal.confirmText}
                        cancelText={confirmationModal.cancelText}
                    />

                    <WhatsAppModal
                        isOpen={whatsappModal.isOpen}
                        onClose={() => setWhatsappModal({ ...whatsappModal, isOpen: false })}
                        link={whatsappModal.link}
                        status={whatsappModal.status}
                    />
                </div>
            </div>
        </>
    );
};

export default MatchPage;